/**
 * «Plantilla de Meta» de un grupo que va por el WhatsApp oficial (R3 del rediseño, 29/10/2026): por ahí no sale el texto
 * de Mensajes sino una plantilla aprobada por Meta. Es el mismo selector que «Qué les decís» del asistente
 * (`PasoPlantilla`, con la previa de la elegida) y, debajo, qué dato va en cada variable (`MapeoVariablesForm`, con los
 * datos de una audiencia de Activa: la oferta, el código del cupón y su vencimiento salen del grupo de cada persona).
 *
 * - Al elegir una plantilla el mapeo arranca con lo que sugiere (o con el de otro grupo que ya la usa: con el mismo mapeo
 *   salen en la misma campaña del oficial). Cambiar la de un grupo con variables elegidas pregunta antes, en línea.
 * - Crear, partir de un modelo, duplicar o seguir editando una plantilla se abre en otra pestaña: acá queda todo lo que no
 *   se guardó, y al volver la lista se actualiza sola (`usePlantillasDeMeta`).
 * - No calcula nada de lo que sale: los avisos (qué falta, la decisión de cada dato, el cupón) son los del backend, y lo
 *   que le llega a cada persona lo calcula el oficial en «Revisar y mandar».
 * - Con la plantilla creada desde la propuesta del grupo («Crear las plantillas en Meta», la parte C), el mapeo y el «si
 *   falta» vienen propuestos, y lo dice: el operador los confirma o los cambia acá, como cualquier otro.
 */

import { useEffect, useMemo, useState } from "react";
import { useCuentaOficialOpcional } from "../../wa-oficial/contexto";
import { oficialApi } from "../../wa-oficial/api";
import { llamar } from "../../wa-oficial/components/base/comun";
import { MapeoVariablesForm } from "../../wa-oficial/components/MapeoVariablesForm";
import { PasoPlantilla, PreguntaCambioPlantilla } from "../../wa-oficial/components/asistente/PasoPlantilla";
import { variablesSinClics } from "../../wa-oficial/logic/clics";
import { imagenPorCampana } from "../../wa-oficial/logic/imagenes";
import { mapeoInicial, validarMapeoLocal } from "../../wa-oficial/logic/mapeo";
import { textoError } from "../../wa-oficial/logic/respuesta";
import type { CampoDisponible, MapeoVariables, PlantillaDetalle, PlantillaResumen } from "../../wa-oficial/types";
import type { PlantillasDeMeta } from "../hooks/usePlantillasDeMeta";
import { cambiarMapeo, elegirPlantilla, type Borrador } from "../logic/mensajes";

const RUTA_PLANTILLAS = "/activa/oficial/plantillas";

/** El editor de plantillas, en otra pestaña: lo que no se guardó en Mensajes queda acá. */
const abrir = (ruta: string) => window.open(ruta, "_blank", "noopener");

export function PlantillaDelGrupo({
  b,
  guardadaId,
  meta,
  campos,
  deOtroGrupo,
  mapeoPropuesto = false,
  alCambiar,
}: {
  b: Borrador;
  /** la plantilla que el grupo ya tiene guardada: se marca aunque esté rechazada o sea un borrador */
  guardadaId: string;
  meta: PlantillasDeMeta;
  campos: CampoDisponible[];
  /** el mapeo de otro grupo que ya usa esa plantilla (`mapeoDeOtroGrupo`), o null */
  deOtroGrupo: (plantillaId: string) => MapeoVariables | null;
  /** la plantilla es la que se creó desde la propuesta del grupo: el mapeo y el «si falta» los propuso el sistema */
  mapeoPropuesto?: boolean;
  alCambiar: (b: Borrador) => void;
}) {
  const modo = useCuentaOficialOpcional()?.modo ?? null;
  const [pendiente, setPendiente] = useState<PlantillaResumen | null>(null);
  const [duplicandoId, setDuplicandoId] = useState("");
  const [errorDuplicar, setErrorDuplicar] = useState("");
  const plantillaId = b.plantillaId ?? "";
  const detalle: PlantillaDetalle | undefined = plantillaId ? meta.detalles[plantillaId] : undefined;
  const { pedirDetalle } = meta;
  const variables = useMemo(() => variablesSinClics(detalle?.variables, detalle?.botones), [detalle]);
  const camposValidos = useMemo(() => campos.map((c) => String(c.campo)), [campos]);
  const mapeo = b.mapeo ?? {};

  useEffect(() => {
    if (plantillaId) void pedirDetalle(plantillaId);
  }, [plantillaId, pedirDetalle]);

  // una plantilla recién elegida (sin mapeo todavía): el mapeo arranca con lo que sugieren sus variables
  useEffect(() => {
    if (!detalle || detalle.id !== plantillaId || b.mapeo) return;
    alCambiar(cambiarMapeo(b, mapeoInicial(variables, undefined, camposValidos.length ? camposValidos : undefined)));
  }, [detalle, plantillaId, b, variables, camposValidos, alCambiar]);

  const usar = (p: PlantillaResumen) => {
    setPendiente(null);
    alCambiar(elegirPlantilla(b, p.id, deOtroGrupo(p.id)));
  };
  const elegir = (p: PlantillaResumen) => {
    if (p.id === plantillaId) return;
    if (Object.keys(mapeo).length) setPendiente(p);
    else usar(p);
  };

  const duplicar = async (id: string) => {
    // la pestaña se abre con el toque: abierta después de esperar al servidor, el navegador la puede bloquear
    const pestana = window.open("", "_blank");
    setDuplicandoId(id);
    setErrorDuplicar("");
    const l = await llamar<PlantillaDetalle>(() => oficialApi.duplicarPlantilla(id));
    setDuplicandoId("");
    if (!l.ok || !l.data) {
      pestana?.close();
      setErrorDuplicar(l.ok ? "No pudimos duplicar la plantilla." : `No pudimos duplicar la plantilla. ${textoError(l)}`);
      return;
    }
    if (pestana) pestana.location.href = `${RUTA_PLANTILLAS}/${encodeURIComponent(l.data.id)}/editar`;
  };

  return (
    <div className="space-y-4" data-testid="plantilla-del-grupo">
      <PasoPlantilla
        plantillas={meta.plantillas}
        error={meta.error}
        plantillaId={plantillaId}
        onElegir={elegir}
        detalle={detalle}
        errorDetalle={plantillaId ? (meta.errores[plantillaId] ?? "") : ""}
        modo={modo}
        asociadaId={guardadaId}
        onCrear={() => abrir(`${RUTA_PLANTILLAS}/nueva`)}
        onModelos={() => abrir(`${RUTA_PLANTILLAS}/modelos`)}
        onDuplicar={(id) => void duplicar(id)}
        onSeguirEditando={(id) => abrir(`${RUTA_PLANTILLAS}/${encodeURIComponent(id)}/editar`)}
        duplicandoId={duplicandoId}
        estadoEnVivo={null}
      />
      <p className="text-[13px] text-slate-500">Crear o corregir una plantilla se abre en otra pestaña: al volver acá, la lista se actualiza sola.</p>
      {errorDuplicar && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorDuplicar}
        </p>
      )}
      {pendiente && <PreguntaCambioPlantilla onSi={() => usar(pendiente)} onNo={() => setPendiente(null)} guardando={false} />}
      {plantillaId && detalle && (
        <div className="space-y-3 border-t border-slate-100 pt-4">
          <h4 className="text-base font-semibold text-slate-900">Lo que va en cada variable</h4>
          <p className="text-[13px] text-slate-600">
            La oferta, el código del cupón y su vencimiento salen del grupo de cada persona. Para cada dato, elegí qué hacer si a alguien le falta.
          </p>
          {mapeoPropuesto && (
            <p className="rounded-lg bg-violet-50 px-3 py-2 text-[13px] text-violet-900" data-testid="mapeo-propuesto">
              Lo propusimos al crear la plantilla con la propuesta del grupo: si falta el nombre, la variable queda vacía; si falta el cupón, la oferta o el
              vencimiento, no se le manda; si falta la sucursal o la marca, va el nombre de la marca. Confirmalo o cambialo.
            </p>
          )}
          <MapeoVariablesForm
            variables={variables}
            campos={campos}
            mapeo={mapeo}
            sinResolver={[]}
            onChange={(m) => alCambiar(cambiarMapeo(b, m))}
            errores={validarMapeoLocal(
              variables.map((v) => v.numero),
              mapeo,
              camposValidos.length ? camposValidos : undefined,
            )}
            imagen={imagenPorCampana(detalle.mediaUrl)}
          />
        </div>
      )}
    </div>
  );
}
